<template>
  <div class="rights-assignment-editor" v-loading="loading">
    <el-alert
      title="先选择版权档案，再设置当前资源的例外和展示范围"
      type="info"
      :closable="false"
      show-icon>
      合同、许可和证明文件只保存在版权档案中；当前页面不会复制公共信息。
    </el-alert>

    <section class="editor-section">
      <div class="section-heading">
        <div>
          <span class="step-index">1</span>
          <strong>关联版权档案</strong>
          <p>优先搜索已有档案，只有确实不存在时才新建。</p>
        </div>
        <el-tag :type="assignment.record_id ? 'success' : 'info'">
          {{ assignment.record_id ? '已关联' : '尚未关联' }}
        </el-tag>
      </div>

      <SoundRightsRecordSelector
        v-model="assignment.record_id"
        @change="handleRecordChange" />

      <template v-if="selectedRecord">
        <div class="effective-summary">
          <div>
            <span>最终版权情况</span>
            <strong>{{ optionLabel(options.rights_statuses, effectiveRights.rights_status, '版权情况未知') }}</strong>
          </div>
          <div>
            <span>审核进度</span>
            <strong>{{ optionLabel(options.verification_statuses, effectiveRights.verification_status, '未审核') }}</strong>
          </div>
          <div>
            <span>风险等级</span>
            <strong>{{ optionLabel(options.risk_levels, effectiveRights.risk_level, '未判断') }}</strong>
          </div>
          <div>
            <span>证明文件</span>
            <strong>{{ selectedRecord.evidence_count || 0 }} 份</strong>
          </div>
        </div>

        <div class="rights-public-setting">
          <div>
            <strong>向客户端展示版权信息</strong>
            <p>
              默认关闭。只有确认当前版权档案适合对用户公开时再开启；
              关闭不会影响内部审核和展示策略。
            </p>
          </div>
          <div class="rights-public-control">
            <el-tag size="small" :type="assignment.show_rights_info ? 'success' : 'info'">
              {{ assignment.show_rights_info ? '客户端可见' : '仅后台可见' }}
            </el-tag>
            <el-switch
              v-model="assignment.show_rights_info"
              :active-value="1"
              :inactive-value="0"
              @change="markChanged" />
          </div>
        </div>

        <el-collapse v-model="overridePanels" class="override-collapse">
          <el-collapse-item name="resource-overrides">
            <template slot="title">
              <i class="el-icon-setting"></i>
              当前资源的独立信息
              <small>不修改共享档案，也不影响其他资源</small>
            </template>

            <el-row :gutter="16">
              <el-col :span="12">
                <label class="field-label">当前资源原始发布页</label>
                <el-input
                  v-model.trim="assignment.source_url_override"
                  maxlength="1000"
                  placeholder="留空则使用档案的共享来源页"
                  @input="markChanged" />
              </el-col>
              <el-col :span="12">
                <label class="field-label">当前资源署名文本</label>
                <el-input
                  v-model.trim="assignment.attribution_text_override"
                  maxlength="1000"
                  placeholder="留空则使用档案的默认署名"
                  @input="markChanged" />
              </el-col>
            </el-row>

            <el-row :gutter="16" class="mt12">
              <el-col :span="12">
                <label class="field-label">审核进度覆盖</label>
                <el-select
                  v-model="assignment.verification_status_override"
                  clearable
                  placeholder="继承版权档案"
                  style="width:100%"
                  @change="markChanged">
                  <el-option
                    v-for="item in options.verification_statuses"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value" />
                </el-select>
              </el-col>
              <el-col :span="12">
                <label class="field-label">风险等级覆盖</label>
                <el-select
                  v-model="assignment.risk_level_override"
                  clearable
                  placeholder="继承版权档案"
                  style="width:100%"
                  @change="markChanged">
                  <el-option
                    v-for="item in options.risk_levels"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value" />
                </el-select>
              </el-col>
            </el-row>

            <div class="mt12">
              <label class="field-label">当前资源审核说明</label>
              <el-input
                v-model.trim="assignment.review_note"
                type="textarea"
                :rows="3"
                maxlength="5000"
                show-word-limit
                placeholder="只记录当前资源的例外、来源差异或特殊风险"
                @input="markChanged" />
            </div>
          </el-collapse-item>
        </el-collapse>
      </template>
    </section>

    <section class="editor-section">
      <div class="section-heading">
        <div>
          <span class="step-index">2</span>
          <strong>推荐与展示控制</strong>
          <p>“沿用当前展示”不会写入策略记录，客户端继续使用现有 sounds 规则。</p>
        </div>
        <el-tag :type="policyTagType">{{ policyLabel }}</el-tag>
      </div>

      <div class="preset-grid">
        <button
          v-for="preset in options.presets"
          :key="preset.value"
          type="button"
          class="preset-card"
          :class="{ active: exposurePolicy.preset_code === preset.value }"
          @click="selectPreset(preset)">
          <span class="preset-radio">
            <i v-if="exposurePolicy.preset_code === preset.value" class="el-icon-check"></i>
          </span>
          <span class="preset-content">
            <strong>{{ preset.label }}</strong>
            <small>{{ preset.description }}</small>
          </span>
        </button>
      </div>

      <div class="policy-preview">
        <span>实际效果</span>
        <el-tag
          v-for="item in policyCapabilities"
          :key="item.key"
          size="small"
          :type="item.inherited ? 'info' : (item.allowed ? 'success' : 'info')"
          :effect="item.allowed && !item.inherited ? 'light' : 'plain'">
          {{ item.inherited ? `${item.label}·现有规则` : `${item.allowed ? '✓' : '×'} ${item.label}` }}
        </el-tag>
      </div>

      <div v-if="exposurePolicy.preset_code !== 'inherit'" class="mt12">
        <label class="field-label">设置原因（必填）</label>
        <el-input
          v-model.trim="exposurePolicy.override_reason"
          type="textarea"
          :rows="2"
          maxlength="1000"
          show-word-limit
          placeholder="例如：商业歌曲，不进入推荐；版权敏感，仅搜索可见"
          @input="markChanged" />
      </div>
    </section>

    <div v-if="history.length" class="history-tip">
      <i class="el-icon-time"></i>
      已保存 {{ history.length }} 条最近关联与展示策略变更摘要。
    </div>

    <div v-if="showSaveButton" class="editor-actions">
      <span v-if="!soundId">音效创建成功后会自动保存本页选择</span>
      <el-button v-else type="primary" :loading="saving" icon="el-icon-check" @click="save">
        保存版权关联与展示策略
      </el-button>
    </div>
  </div>
</template>

<script>
import apis from '@/api/index'
import SoundRightsRecordSelector from '@/components/sounds/SoundRightsRecordSelector.vue'
import { exposurePresetTagType, optionLabel } from '@/lib/soundAdminUi'

const POLICY_FIELDS = [
  'allow_search',
  'allow_category',
  'allow_recent',
  'allow_recommend',
  'allow_favorite',
  'allow_detail',
  'allow_preview',
  'allow_download'
]

export default {
  name: 'SoundRightsAssignmentEditor',
  components: {
    SoundRightsRecordSelector
  },
  props: {
    soundId: {
      type: [Number, String],
      default: null
    },
    showSaveButton: {
      type: Boolean,
      default: true
    }
  },
  /**
   * 创建版权关联和展示策略草稿。
   *
   * @returns {Object}
   */
  data() {
    return {
      loading: false,
      saving: false,
      changed: false,
      selectedRecord: null,
      history: [],
      overridePanels: [],
      options: {
        rights_statuses: [],
        verification_statuses: [],
        risk_levels: [],
        presets: []
      },
      assignment: this.createDefaultAssignment(),
      exposurePolicy: this.createDefaultPolicy()
    }
  },
  computed: {
    /**
     * 合并共享档案和当前资源覆盖项。
     *
     * @returns {Object}
     */
    effectiveRights() {
      const record = this.selectedRecord || {}
      return {
        rights_status: record.rights_status || 'unknown',
        verification_status: this.assignment.verification_status_override || record.verification_status || 'unreviewed',
        risk_level: this.assignment.risk_level_override || record.risk_level || 'unknown',
        source_url: this.assignment.source_url_override || record.source_url || '',
        attribution_text: this.assignment.attribution_text_override || record.attribution_template || ''
      }
    },

    /**
     * 返回展示策略中文名称。
     *
     * @returns {String}
     */
    policyLabel() {
      return this.optionLabel(this.options.presets, this.exposurePolicy.preset_code, '沿用当前展示')
    },

    /**
     * 返回展示策略摘要标签颜色。
     *
     * @returns {String}
     */
    policyTagType() {
      return exposurePresetTagType(this.exposurePolicy.preset_code)
    },

    /**
     * 返回当前展示策略各入口的易读摘要。
     *
     * @returns {Array}
     */
    policyCapabilities() {
      const labels = {
        allow_search: '搜索',
        allow_category: '分类',
        allow_recent: '最近更新',
        allow_recommend: '推荐',
        allow_favorite: '收藏',
        allow_detail: '详情',
        allow_preview: '试听',
        allow_download: '下载'
      }
      return POLICY_FIELDS.map(key => ({
        key,
        label: labels[key],
        inherited: this.exposurePolicy.preset_code === 'inherit',
        allowed: Number(this.exposurePolicy[key]) === 1
      }))
    }
  },
  watch: {
    /**
     * 切换音效时重新读取关联，避免显示上一条资源数据。
     *
     * @param {Number|String} value 新音效 ID
     * @param {Number|String} oldValue 旧音效 ID
     * @returns {void}
     */
    soundId(value, oldValue) {
      if (String(value || '') === String(oldValue || '')) return
      if (value) this.load()
      else this.resetEditor()
    }
  },
  /**
   * 加载统一选项和已有音效数据。
   *
   * @returns {Promise<void>}
   */
  async created() {
    await this.loadOptions()
    if (this.soundId) await this.load()
  },
  methods: {
    optionLabel,
    /**
     * 创建未关联版权档案的默认关系。
     *
     * @returns {Object}
     */
    createDefaultAssignment() {
      return {
        record_id: null,
        source_url_override: '',
        attribution_text_override: '',
        verification_status_override: null,
        risk_level_override: null,
        show_rights_info: 0,
        review_note: ''
      }
    },

    /**
     * 创建“无策略记录即沿用当前显示”的默认策略。
     *
     * @returns {Object}
     */
    createDefaultPolicy() {
      return {
        preset_code: 'inherit',
        allow_search: 1,
        allow_category: 1,
        allow_recent: 1,
        allow_recommend: 1,
        allow_favorite: 1,
        allow_detail: 1,
        allow_preview: 1,
        allow_download: 1,
        override_reason: ''
      }
    },

    /**
     * 重置本地草稿。
     *
     * @returns {void}
     */
    resetEditor() {
      this.assignment = this.createDefaultAssignment()
      this.exposurePolicy = this.createDefaultPolicy()
      this.selectedRecord = null
      this.history = []
      this.changed = false
    },

    /**
     * 加载版权说明和展示预设。
     *
     * @returns {Promise<void>}
     */
    async loadOptions() {
      try {
        const response = await apis.soundRights.options()
        if (response.data.code !== 1) throw new Error(response.data.msg || '版权选项加载失败')
        this.options = { ...this.options, ...(response.data.data || {}) }
      } catch (error) {
        this.$message.error(error.message || String(error))
      }
    },

    /**
     * 加载当前音效的版权关联和展示策略。
     *
     * @returns {Promise<void>}
     */
    async load() {
      const id = Number(this.soundId || 0)
      if (!id) return
      this.loading = true
      try {
        const response = await apis.soundRights.read({ sound_id: id })
        if (response.data.code !== 1) throw new Error(response.data.msg || '版权关联加载失败')
        const data = response.data.data || {}
        const assignment = data.assignment || null
        this.assignment = {
          ...this.createDefaultAssignment(),
          ...((assignment && assignment.relation) || {}),
          record_id: assignment && assignment.record ? Number(assignment.record.id) : null
        }
        this.assignment.show_rights_info =
          Number(this.assignment.show_rights_info) === 1 ? 1 : 0
        this.selectedRecord = assignment ? assignment.record : null
        this.exposurePolicy = { ...this.createDefaultPolicy(), ...(data.exposure_policy || {}) }
        this.history = data.history || []
        this.changed = false
      } catch (error) {
        this.$message.error(error.message || String(error))
      } finally {
        this.loading = false
      }
    },

    /**
     * 响应版权档案选择或编辑结果。
     *
     * @param {Object|null} record 最新档案
     * @returns {void}
     */
    handleRecordChange(record) {
      const oldRecordId = Number(this.assignment.record_id || 0)
      const newRecordId = record ? Number(record.id) : 0
      this.selectedRecord = record || null
      this.assignment.record_id = newRecordId || null
      // 切换为另一份版权档案时重新回到默认不公开，防止沿用上一份
      // 档案的公开决定。重新选择当前档案不会打断工作人员的编辑。
      if (oldRecordId !== newRecordId) this.assignment.show_rights_info = 0
      this.markChanged()
    },

    /**
     * 标记页面草稿发生变化。
     *
     * @returns {void}
     */
    markChanged() {
      this.changed = true
    },

    /**
     * 选择展示预设并应用服务端返回的矩阵。
     *
     * @param {Object} preset 展示预设
     * @returns {void}
     */
    selectPreset(preset) {
      if (!preset) return
      this.exposurePolicy = {
        ...this.exposurePolicy,
        ...(preset.policy || {}),
        preset_code: preset.value
      }
      if (preset.value === 'inherit') {
        this.exposurePolicy.override_reason = ''
      }
      this.markChanged()
    },

    /**
     * 判断新建音效页面是否存在需要保存的草稿。
     *
     * @returns {Boolean}
     */
    hasMeaningfulDraft() {
      return Boolean(this.assignment.record_id) || this.exposurePolicy.preset_code !== 'inherit'
    },

    /**
     * 保存草稿到指定音效。
     *
     * 添加音效页面在获得新 sound_id 后调用同一方法，不需要第二套逻辑。
     *
     * @param {Number|String} targetSoundId 音效 ID
     * @returns {Promise<Object|null>}
     */
    async persistDraft(targetSoundId) {
      const id = Number(targetSoundId || this.soundId || 0)
      if (!id) throw new Error('缺少音效 ID')
      if (!this.soundId && !this.hasMeaningfulDraft()) return null
      if (this.exposurePolicy.preset_code !== 'inherit' && !this.exposurePolicy.override_reason) {
        throw new Error('设置新的展示策略时，请填写设置原因')
      }

      const response = await apis.soundRights.save({
        sound_id: id,
        assignment: this.assignment,
        exposure_policy: this.exposurePolicy
      })
      if (response.data.code !== 1) throw new Error(response.data.msg || '保存失败')
      this.changed = false
      this.$emit('saved', { sound_id: id, ...(response.data.data || {}) })
      return response.data.data || {}
    },

    /**
     * 保存当前音效并刷新服务端最终结果。
     *
     * @returns {Promise<void>}
     */
    async save() {
      this.saving = true
      try {
        await this.persistDraft(this.soundId)
        this.$message.success('版权关联与展示策略已保存')
        await this.load()
      } catch (error) {
        this.$message.error(error.message || String(error))
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.rights-assignment-editor {
  color: #303133;
}

.editor-section {
  margin-top: 16px;
  padding: 18px;
  border: 1px solid #e4e7ed;
  border-radius: 10px;
  background: #fff;
}

.section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;

  strong {
    font-size: 17px;
  }

  p {
    margin: 5px 0 0 34px;
    color: #909399;
    font-size: 12px;
  }
}

.step-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-right: 8px;
  border-radius: 50%;
  color: #fff;
  background: #409eff;
  font-size: 13px;
}

.effective-summary {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-top: 14px;

  > div {
    display: flex;
    padding: 12px;
    border-radius: 7px;
    background: #f7f9fc;
    flex-direction: column;
    gap: 5px;
  }

  span {
    color: #909399;
    font-size: 12px;
  }
}

.rights-public-setting {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-top: 14px;
  padding: 14px;
  border: 1px solid #dfe7f3;
  border-radius: 8px;
  background: #f7faff;

  strong {
    font-size: 14px;
  }

  p {
    margin: 5px 0 0;
    color: #8492a6;
    font-size: 12px;
    line-height: 1.55;
  }
}

.rights-public-control {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 12px;
}

.override-collapse {
  margin-top: 14px;

  small {
    margin-left: 10px;
    color: #909399;
  }
}

.field-label {
  display: block;
  margin-bottom: 7px;
  color: #606266;
  font-size: 13px;
  font-weight: 600;
}

.mt12 {
  margin-top: 12px;
}

.preset-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.preset-card {
  display: flex;
  min-height: 76px;
  padding: 13px;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  color: inherit;
  background: #fff;
  text-align: left;
  cursor: pointer;

  &.active {
    border-color: #409eff;
    background: #f2f8ff;
  }
}

.preset-radio {
  display: inline-flex;
  flex: 0 0 20px;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-right: 10px;
  border: 1px solid #c0c4cc;
  border-radius: 50%;

  .active & {
    border-color: #409eff;
    color: #fff;
    background: #409eff;
  }
}

.preset-content {
  display: flex;
  flex-direction: column;
  gap: 6px;

  small {
    color: #909399;
    line-height: 1.45;
  }
}

.policy-preview {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
}

.history-tip {
  margin-top: 12px;
  color: #909399;
  font-size: 12px;
}

.editor-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 16px;

  span {
    color: #909399;
    font-size: 12px;
  }
}

@media (max-width: 1100px) {
  .preset-grid {
    grid-template-columns: 1fr;
  }

  .effective-summary {
    grid-template-columns: repeat(2, 1fr);
  }

  .rights-public-setting {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
